// ============================================================
// Card Asset System
// ============================================================
// Unterstützt Sets mit "baseSet" + "overrides":
//   - classic2 überschreibt nur J, Q, K → Rest kommt von classic
//   - Neue Sets können dasselbe Prinzip nutzen

export interface CardSetManifest {
  id: string;
  name: string;
  preview: string;
  /** Optionales Basis-Set für Karten die nicht in diesem Set vorhanden sind */
  baseSet?: string;
  /** Liste der card.id's die dieses Set überschreibt (nur wenn baseSet gesetzt) */
  overrides?: string[];
  backs: string[];
  defaultBack: string;
  /**
   * Seitenverhältnis der Karten: Höhe / Breite.
   * Standard: 1.5 (2:3 Ratio). Wird für die Tableau-Overlap-Formel benötigt.
   */
  cardAspectRatio?: number;
  /**
   * Sichtbarer Bereich einer verdeckten Karte in px (max, Desktop).
   * Standard: 14px.
   */
  faceDownPeek?: number;
  /**
   * Sichtbarer Bereich einer aufgedeckten Karte in px (max, Desktop).
   * Standard: 32px.
   */
  faceUpPeek?: number;
}

const BASE_PATH = "/cards/sets";

/**
 * Gibt die URL zur Vorderseite einer Karte zurück.
 *
 * Logik:
 *   1. Hat das Set ein baseSet + overrides?
 *      → Liegt die cardId in overrides? → eigenes Set verwenden
 *      → Sonst → baseSet verwenden
 *   2. Kein baseSet → immer eigenes Set
 */
export function getCardFaceUrl(
  cardId: string,
  setId: string,
  manifests: CardSetManifest[]
): string {
  const manifest = manifests.find((m) => m.id === setId);

  if (manifest?.baseSet && manifest.overrides) {
    const useOwnSet = manifest.overrides.includes(cardId);
    const resolvedSet = useOwnSet ? setId : manifest.baseSet;
    return `${BASE_PATH}/${resolvedSet}/faces/${cardId}.png`;
  }

  return `${BASE_PATH}/${setId}/faces/${cardId}.png`;
}

/**
 * Gibt die URL zum Kartenrücken zurück.
 * Falls das Set ein baseSet hat und keinen eigenen Rücken definiert,
 * wird der Rücken vom baseSet verwendet.
 */
export function getCardBackUrl(
  setId: string,
  backName: string,
  manifests: CardSetManifest[]
): string {
  const manifest = manifests.find((m) => m.id === setId);
  const resolvedSet =
    manifest?.baseSet && !manifest.backs.includes(backName)
      ? manifest.baseSet
      : setId;
  return `${BASE_PATH}/${resolvedSet}/backs/${backName}.png`;
}
